import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ImpactCalculator = () => {
  const [contribution, setContribution] = useState({
    amount: '',
    type: 'monthly',
    category: 'general'
  });

  const [results, setResults] = useState(null);

  const impactRates = {
    education: {
      rate: 500, // LKR per student per month
      unit: 'students',
      description: 'students can receive educational support'
    },
    healthcare: {
      rate: 300, // LKR per person per month
      unit: 'people',
      description: 'people can access basic healthcare services'
    },
    environment: {
      rate: 200, // LKR per tree
      unit: 'trees',
      description: 'trees can be planted and maintained'
    },
    general: {
      rate: 400, // Average impact rate
      unit: 'people',
      description: 'community members can be directly helped'
    }
  };

  const calculateImpact = () => {
    if (!contribution.amount || contribution.amount <= 0) return;

    const amount = parseFloat(contribution.amount);
    const multiplier = contribution.type === 'monthly' ? 12 : 1;
    const totalAmount = amount * multiplier;
    
    const rate = impactRates[contribution.category];
    const directBeneficiaries = Math.floor(totalAmount / rate.rate);
    const indirectBeneficiaries = Math.floor(directBeneficiaries * 2.5); // Family/community impact
    
    setResults({
      totalAmount,
      directBeneficiaries,
      indirectBeneficiaries,
      category: contribution.category,
      description: rate.description,
      unit: rate.unit
    });
  };

  const resetCalculator = () => {
    setContribution({ amount: '', type: 'monthly', category: 'general' });
    setResults(null);
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Impact Calculator
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Discover how your contribution translates into real community impact. See the tangible difference your support can make.
          </p>
        </div>

        <div className="bg-card rounded-xl p-8 card-shadow">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-foreground mb-4">Your Contribution</h3>
              
              <div className="space-y-4">
                <Input
                  label="Contribution Amount (LKR)"
                  type="number"
                  placeholder="Enter amount"
                  value={contribution.amount}
                  onChange={(e) => setContribution({...contribution, amount: e.target.value})}
                  className="text-lg"
                />

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Contribution Type</label>
                  <div className="flex space-x-4">
                    {[
                      { value: 'monthly', label: 'Monthly' },
                      { value: 'annual', label: 'One-time' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setContribution({...contribution, type: option.value})}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                          contribution.type === option.value
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-surface text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Impact Category</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'education', label: 'Education', icon: 'GraduationCap' },
                      { value: 'healthcare', label: 'Healthcare', icon: 'Heart' },
                      { value: 'environment', label: 'Environment', icon: 'Leaf' },
                      { value: 'general', label: 'General Support', icon: 'Users' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setContribution({...contribution, category: option.value})}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                          contribution.category === option.value
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-surface text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={option.icon} size={16} />
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="default"
                    onClick={calculateImpact}
                    disabled={!contribution.amount}
                    className="hero-gradient flex-1"
                    iconName="Calculator"
                    iconPosition="left"
                  >
                    Calculate Impact
                  </Button>
                  <Button
                    variant="outline"
                    onClick={resetCalculator}
                    iconName="RotateCcw"
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-foreground mb-4">Projected Impact</h3>
              
              {results ? (
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-emerald-700 mb-2">
                        {results.directBeneficiaries}
                      </div>
                      <div className="text-emerald-600 font-medium">
                        {results.description}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-surface rounded-lg p-4 text-center">
                      <div className="text-xl font-bold text-foreground">{results.indirectBeneficiaries}</div>
                      <div className="text-sm text-muted-foreground">Indirect beneficiaries</div>
                    </div>
                    <div className="bg-surface rounded-lg p-4 text-center">
                      <div className="text-xl font-bold text-foreground">LKR {results.totalAmount.toLocaleString()}</div>
                      <div className="text-sm text-muted-foreground">Annual impact</div>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Icon name="Lightbulb" size={20} className="text-amber-600 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <strong>Impact Multiplier:</strong> Your contribution creates a ripple effect, 
                        indirectly benefiting families and communities beyond the direct recipients.
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="default"
                    fullWidth
                    className="achievement-gradient"
                    iconName="Heart"
                    iconPosition="left"
                  >
                    Make This Impact Real
                  </Button>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Calculator" size={64} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Enter your contribution details to see the projected community impact
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            * Impact calculations are based on our historical project data and average costs. 
            Actual impact may vary depending on specific project requirements and local conditions.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ImpactCalculator;